import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";

import {
  apiGetEventsCategoriesOptions,
  apiGetPatientsByPatientIdEventsOptions,
  DefaultPatientEventResponse,
  ListEventCategories,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { CellProps } from "../../../components/ui-kit/tables/cells/cell";
import { CellEnum } from "../../../components/ui-kit/tables/cells/cell-enum";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { uniqueValueOptions } from "../../../utils/filter-presets";
import { requireNonNull } from "../../../utils/utils";
import { PatientSubpage } from "../common/patient-subpage";
import { AddEventsAction } from "./add-events-action";
import {
  eventCategory,
  EventCategoryDetails,
  EventCategoryType,
  eventDetailsEnum,
  eventTypesEnum,
} from "./enums";
import { Events } from "./events";

export const EventsTable = observer(function EventsTable(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const [model] = useState(
    () => new Events([], api.permissions, api.requireUserUUID()),
  );

  const eventsQuery = useQuery(
    apiGetPatientsByPatientIdEventsOptions({
      path: { patient_id: patient.patientId },
    }),
  );
  // Each event carries its creator, so an admin who isn't connected to the
  // patient still shows by name.
  const userMapping = useMemo(
    () =>
      Object.fromEntries(
        (eventsQuery.data?.events ?? []).flatMap((event) =>
          event.user ? [[event.user.uuid, event.user.username] as const] : [],
        ),
      ),
    [eventsQuery.data],
  );
  const categoriesQuery = useQuery(apiGetEventsCategoriesOptions());
  const eventOptions = categoriesQuery.data ?? null;
  const loading = !model.initialized || categoriesQuery.isPending;
  const refetch = async () => {
    await Promise.all([eventsQuery.refetch(), categoriesQuery.refetch()]);
  };

  useEffect(() => {
    if (eventsQuery.data) model.updateData(eventsQuery.data.events);
  }, [eventsQuery.data, model]);

  return (
    <PatientSubpage title={t`Events`} loading={loading}>
      <DataTable
        filterActions={
          <AddEventsAction
            app={props.app}
            eventOptions={eventOptions}
            onSaved={refetch}
          />
        }
        persistState
        editMode="dialog"
        model={model}
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "event", other: "events" })}`
        }
        columns={[
          {
            header: <Trans>Date</Trans>,
            attr: "event_date",
            type: "date",
            editable: true,
            sort: true,
            // Several events commonly share one date; order those by when they
            // were recorded rather than leaving them in API order.
            sortTiebreakBy: "event_timestamp",
            initSort: "desc",
          },
          {
            header: <Trans>Timestamp</Trans>,
            attr: "event_timestamp",
            type: "datetime",
            sort: true,
          },
          {
            header: <Trans>Category</Trans>,
            attr: "event_category",
            type: "enum",
            values: eventCategory,
            filter: {
              kind: "multi",
              group: t`Event`,
              options: new Map(Object.entries(eventCategory)),
            },
          },
          {
            header: <Trans>Category Type</Trans>,
            attr: "event_category_type",
            filter: {
              kind: "multi",
              group: t`Event`,
              options: uniqueValueOptions("event_category_type"),
            },
            editable: (item) => typeOptionsFor(eventOptions, item).length > 0,
            editInDialog: true,
            cell: (p) => (
              <EventCategoryTypeCell
                cellProps={p}
                eventOptions={eventOptions}
              />
            ),
          },
          {
            header: <Trans>Category Details</Trans>,
            attr: "event_category_details",
            filter: {
              kind: "multi",
              group: t`Event`,
              options: uniqueValueOptions("event_category_details"),
            },
            editable: (item) => categoryHasDetails(eventOptions, item),
            editInDialog: true,
            cell: (p) => (
              <EventCategoryDetailsCell
                cellProps={p}
                eventOptions={eventOptions}
              />
            ),
          },
          {
            header: <Trans>Description</Trans>,
            attr: "event_description",
            type: "richtext",
            editable: true,
            width: 330,
          },
          {
            header: <Trans>Comment</Trans>,
            attr: "event_comment",
            type: "richtext",
            editable: true,
            width: 330,
          },
          {
            header: <Trans>Created by</Trans>,
            attr: "user_uuid",
            type: "enum",
            values: userMapping,
            filter: {
              kind: "multi",
              group: t`Event`,
              options: getCreatedByOptions(userMapping),
            },
          },
        ]}
        onEdit={async (editedField, item) => {
          // Changing the category type clears the details (the old value no
          // longer applies), so that null has to survive to the API.
          await patient.updateItem("events", item.uuid, editedField, {
            clearableKeys: ["event_category_details"],
          });
          await refetch();
        }}
        onDelete={async (item) => {
          await patient.deleteItem("events", item.uuid);
          await refetch();
        }}
      />
    </PatientSubpage>
  );
});

const EventCategoryTypeCell = observer(function EventCategoryTypeCell({
  cellProps,
  eventOptions,
}: {
  cellProps: CellProps<DefaultPatientEventResponse>;
  eventOptions: ListEventCategories | null;
}) {
  const options = typeOptionsFor(eventOptions, cellProps.item);
  return !cellProps.patch || options.length > 0 ? (
    <CellEnum
      {...cellProps}
      attr={requireNonNull(cellProps.attr)}
      values={eventTypesEnum(options)}
      onChange={() => {
        cellProps.patch.event_category_details = null;
      }}
    />
  ) : null;
});

const EventCategoryDetailsCell = observer(function EventCategoryDetailsCell({
  cellProps,
  eventOptions,
}: {
  cellProps: CellProps<DefaultPatientEventResponse>;
  eventOptions: ListEventCategories | null;
}) {
  // `item` carries the draft while editing, so switching the category type
  // re-derives this list instead of showing the saved type's options.
  const options = detailsOptionsFor(eventOptions, cellProps.item);
  if (cellProps.patch && options.length === 0) {
    return (
      <span className="text-muted-foreground text-sm">
        <Trans>No details for this type</Trans>
      </span>
    );
  }
  return (
    <CellEnum
      {...cellProps}
      attr={requireNonNull(cellProps.attr)}
      values={eventDetailsEnum(options)}
    />
  );
});

function typeOptionsFor(
  eventOptions: ListEventCategories | null,
  item: DefaultPatientEventResponse,
) {
  return Object.keys(
    eventOptions?.[item.event_category] ?? {},
  ) as EventCategoryType[];
}

function detailsOptionsFor(
  eventOptions: ListEventCategories | null,
  item: DefaultPatientEventResponse,
) {
  const byType = detailsByType(eventOptions, item);
  return (item.event_category_type && byType[item.event_category_type]) || [];
}

/**
 * Whether the details field applies to the event's category at all — keyed to
 * the category rather than the current type, so switching to a type that does
 * have details reveals the dropdown within the same edit.
 */
function categoryHasDetails(
  eventOptions: ListEventCategories | null,
  item: DefaultPatientEventResponse,
) {
  return Object.values(detailsByType(eventOptions, item)).some(
    (details) => (details?.length ?? 0) > 0,
  );
}

function detailsByType(
  eventOptions: ListEventCategories | null,
  item: DefaultPatientEventResponse,
) {
  return (eventOptions?.[item.event_category] ?? {}) as unknown as Record<
    EventCategoryType,
    EventCategoryDetails[]
  >;
}

function getCreatedByOptions(userMapping: Record<string, string>) {
  return new Map(
    Object.entries(userMapping).sort(([, a], [, b]) => a.localeCompare(b)),
  );
}
